import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { services } from '@/data/services';
import { blogArticles } from '@/data/blog-articles';

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

const pages = [
  { title: 'Accueil', path: '/', keywords: 'accueil home' },
  { title: 'À propos', path: '/a-propos', keywords: 'à propos about cyrille fondateur histoire' },
  { title: 'Services', path: '/services', keywords: 'services prestations' },
  { title: 'Portfolio', path: '/portfolio', keywords: 'portfolio galerie photos réalisations' },
  { title: 'FAQ', path: '/faq', keywords: 'faq questions fréquentes' },
  { title: 'Blog', path: '/blog', keywords: 'blog articles actualités' },
  { title: 'Contact', path: '/contact', keywords: 'contact devis rendez-vous téléphone' },
];

const SearchOverlay = ({ open, onClose }: SearchOverlayProps) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (open) onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  const q = query.toLowerCase().trim();

  const matchedPages = q
    ? pages.filter(p => p.title.toLowerCase().includes(q) || p.keywords.includes(q))
    : [];

  const matchedServices = q
    ? services.filter(s =>
        s.title.toLowerCase().includes(q) ||
        s.shortDesc.toLowerCase().includes(q)
      ).slice(0, 5)
    : [];

  const matchedArticles = q
    ? blogArticles.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.metaDescription.toLowerCase().includes(q) ||
        (a.tags || []).some(t => t.toLowerCase().includes(q))
      ).slice(0, 3)
    : [];

  const hasResults = matchedPages.length > 0 || matchedServices.length > 0 || matchedArticles.length > 0;

  const goTo = (path: string) => {
    onClose();
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[1000] bg-petrol/80 backdrop-blur-sm flex items-start justify-center pt-[15vh]"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-xl mx-4 bg-card rounded-2xl border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              <Search size={18} className="text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un service, une page, un article…"
                className="flex-1 bg-transparent text-foreground text-sm placeholder:text-muted-foreground/50 outline-none"
              />
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
                <X size={18} />
              </button>
            </div>

            {/* Results */}
            {q && (
              <div className="max-h-[50vh] overflow-y-auto p-3">
                {!hasResults && (
                  <p className="text-center text-sm text-muted-foreground py-8">
                    Aucun résultat pour « {query} »
                  </p>
                )}

                {matchedPages.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-muted-foreground/50 px-3 mb-1">Pages</p>
                    {matchedPages.map((p) => (
                      <button
                        key={p.path}
                        onClick={() => goTo(p.path)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted/50 transition-colors"
                      >
                        {p.title}
                        <ArrowRight size={14} className="text-muted-foreground" />
                      </button>
                    ))}
                  </div>
                )}

                {matchedServices.length > 0 && (
                  <div className="mb-3">
                    <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-muted-foreground/50 px-3 mb-1">Services</p>
                    {matchedServices.map((s) => (
                      <button
                        key={s.slug}
                        onClick={() => goTo(`/services/${s.slug}`)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted/50 transition-colors text-left"
                      >
                        <s.icon size={15} className="text-primary shrink-0" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{s.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{s.shortDesc.slice(0, 80)}…</p>
                        </div>
                        <ArrowRight size={14} className="text-muted-foreground shrink-0" />
                      </button>
                    ))}
                  </div>
                )}

                {matchedArticles.length > 0 && (
                  <div>
                    <p className="text-[11px] font-heading font-semibold uppercase tracking-wider text-muted-foreground/50 px-3 mb-1">Articles</p>
                    {matchedArticles.map((a) => (
                      <button
                        key={a.slug}
                        onClick={() => goTo(`/blog/${a.slug}`)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-foreground hover:bg-muted/50 transition-colors text-left"
                      >
                        <span className="truncate">{a.title}</span>
                        <ArrowRight size={14} className="text-muted-foreground shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shortcut hint */}
            <div className="px-5 py-2.5 border-t border-border bg-muted/20">
              <p className="text-[11px] text-muted-foreground/50 text-center">
                Appuyez sur <kbd className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px] font-mono">Échap</kbd> pour fermer
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
